import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { Input } from '@/shared/ui/inputs/Input';
import { Textarea } from '@/shared/ui/inputs/Textarea';
import { Button } from '@/shared/ui/buttons/Button';
import { useToast } from '@/shared/hooks/useToast';
import { extractErrorMessage, isApiError } from '@/shared/api/errors';
import { useParcelsAvailableForPhotoVideo } from '@/entities/parcels';
import { useCreateReport } from '@/entities/reports';

type Props = {
  reportType: 'PHOTO' | 'VIDEO' | 'PROVERKA';
};

const BACK_TO: Record<Props['reportType'], string> = {
  PHOTO: '/services/photo',
  VIDEO: '/services/video',
  PROVERKA: '/services/proverka',
};

const TITLE_KEY: Record<Props['reportType'], string> = {
  PHOTO: 'photoTitle',
  VIDEO: 'videoTitle',
  PROVERKA: 'proverkaTitle',
};

export const WidgetsServicesReportFormMain = ({ reportType }: Props) => {
  const { parcelId } = useParams<{ parcelId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('services');
  const toast = useToast();

  const backTo = BACK_TO[reportType];

  const { data, isLoading } = useParcelsAvailableForPhotoVideo(reportType);
  const parcel = (data ?? []).find((p) => p.id === parcelId);

  const schema = z.object({
    contact_info: z.string().min(1, t('contactInfoRequired')),
    note: z.string().optional(),
  });
  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { contact_info: '', note: '' },
    mode: 'onBlur',
  });

  const createReport = useCreateReport();

  const onSubmit = form.handleSubmit((values) => {
    if (!parcelId) return;
    createReport.mutate(
      {
        report_type: reportType,
        parcel: parcelId,
        contact_info: values.contact_info,
        note: values.note || undefined,
      },
      {
        onSuccess: () => {
          toast.success(t('reportCreated'));
          navigate(backTo);
        },
        onError: (err) => {
          // The backend returns hardcoded Uzbek {message} for these cases;
          // surface localized strings instead of echoing the server text to
          // RU/EN clients. 404 = parcel not owned, 400 = duplicate request.
          const status = isApiError(err) ? err.response?.status : undefined;
          if (status === 404) {
            toast.error(t('reportNotOwned'));
            return;
          }
          if (status === 400) {
            toast.error(t('reportDuplicate'));
            return;
          }
          toast.error(extractErrorMessage(err, t('reportError')));
        },
      },
    );
  });

  return (
    <>
      <PageHeader title={t(TITLE_KEY[reportType])} showBack backTo={backTo} />
      <div className="flex flex-col gap-4 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : !parcel ? (
          <EmptyState title={t('selectParcel')} />
        ) : (
          <>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
              <p className="text-base font-semibold text-ink">{parcel.name}</p>
              <p className="mt-0.5 text-xs text-ink-muted">{parcel.trek_id}</p>
              <span className="mt-1.5 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-ink-muted">
                {t(`parcelStatus:${parcel.status}`)}
              </span>
            </div>

            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <Input
                label={t('contactInfo')}
                hint={t('contactInfoHint')}
                required
                error={form.formState.errors.contact_info?.message}
                {...form.register('contact_info')}
              />
              <Textarea label={t('noteOptional')} {...form.register('note')} />
              <Button type="submit" loading={createReport.isPending} block>
                {t('submit')}
              </Button>
            </form>
          </>
        )}
      </div>
    </>
  );
};
